// noinspection JSUnusedGlobalSymbols

import { allThemes, defaultChecklistItems, defaultOptions, State, TempState } from "./state.svelte";
import type { ChecklistItem, Theme } from "./types";
import { DateTime } from "luxon";

function loadStateFromLocalStorage() {
    const stored = {
        count: localStorage.getItem("count") ?? "0",
        percentChecklistItemsComplete: localStorage.getItem("percentChecklistItemsComplete") ?? "0",
        checkListItems: localStorage.getItem("checklistItems") ?? JSON.stringify(defaultChecklistItems),
        lastUpdated: localStorage.getItem("lastUpdated") ?? "",
        lastUpdatedString: localStorage.getItem("lastUpdatedString") ?? "",
        options: {
            theme: localStorage.getItem("theme"),
            darNotifications: localStorage.getItem("darNotifications") ?? "true",
            notificationPermission: localStorage.getItem("notificationPermission") ?? "false",
            keyboardShortcuts: localStorage.getItem("keyboardShortcuts") ?? "false",
        }
    };

    State.count = parseInt(stored.count);
    State.percentChecklistItemsComplete = parseInt(stored.percentChecklistItemsComplete);
    Helper.setChecklist(JSON.parse(stored.checkListItems));
    Helper.forceTheme(Helper.parseTheme(stored.options.theme));
    State.lastUpdated = stored.lastUpdated;
    State.lastUpdatedString = stored.lastUpdatedString;
    State.options.darNotifications = stored.options.darNotifications.toLowerCase() === "true";
    State.options.notificationPermission = stored.options.notificationPermission.toLowerCase() === "true";
    State.options.keyboardShortcuts = stored.options.keyboardShortcuts.toLowerCase() === "true";

    Helper.saveStateToLocalStorage();
}

function saveStateToLocalStorage() {
    localStorage.setItem("count", State.count.toString());
    localStorage.setItem("percentChecklistItemsComplete", State.percentChecklistItemsComplete.toString());
    localStorage.setItem("checklistItems", JSON.stringify(State.checklistItems));
    localStorage.setItem("theme", State.options.theme);
    localStorage.setItem("darNotifications", State.options.darNotifications.toString());
    localStorage.setItem("notificationPermission", State.options.notificationPermission.toString());
    localStorage.setItem("lastUpdated", State.lastUpdated);
    localStorage.setItem("lastUpdatedString", State.lastUpdatedString);
    localStorage.setItem("keyboardShortcuts", State.options.keyboardShortcuts.toString());
}

function rgbToHex(r: number, g: number, b: number): string {
    const toHex = (val: number) => val.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function hsvToRgb(h: number, s: number, v: number) {
    const c = v * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = v - c;

    let rPrime: number, gPrime: number, bPrime: number;

    if (h >= 0 && h < 60) {
        rPrime = c;
        gPrime = x;
        bPrime = 0;
    } else if (h >= 60 && h < 120) {
        rPrime = x;
        gPrime = c;
        bPrime = 0;
    } else if (h >= 120 && h < 180) {
        rPrime = 0;
        gPrime = c;
        bPrime = x;
    } else if (h >= 180 && h < 240) {
        rPrime = 0;
        gPrime = x;
        bPrime = c;
    } else if (h >= 240 && h < 300) {
        rPrime = x;
        gPrime = 0;
        bPrime = c;
    } else {
        rPrime = c;
        gPrime = 0;
        bPrime = x;
    }

    const r = Math.round((rPrime + m) * 255);
    const g = Math.round((gPrime + m) * 255);
    const b = Math.round((bPrime + m) * 255);

    return {r, g, b};
}

function percentageToHexColor(percentage: number): string {
    // Clamp percentage between 0 and 100
    const clamped = Math.max(0, Math.min(100, percentage));

    // Map percentage 0–100 to hue 0–120
    const hue = (120 * clamped) / 100;

    // For "dark", use full brightness (1). For "light", reduce it.
    const brightness = TempState.theme === "black" || TempState.theme === "frappe" ? 1 : 0.7;

    // Full saturation, adjusted brightness
    const {r, g, b} = hsvToRgb(hue, 1, brightness);

    // Convert to HEX string
    return rgbToHex(r, g, b);
}

function updatePercentageEvent() {
    Helper.updatePercentage();
    Helper.setChecklist(State.checklistItems, false);
}

function updatePercentage() {
    let total = 0;
    for (const item of State.checklistItems) {
        for (const option of item.options) {
            if ((option.name === "Yes" || option.name === "N/A") && option.checked) {
                total++;
                break;
            }
        }
    }
    return total / State.checklistItems.length * 100;
}

function setChecklist(items: ChecklistItem[], setDefaultChecked: boolean = true) {
    State.checklistItems = items;

    if (setDefaultChecked) {
        items.forEach(item => {
            item.options.forEach(option => {
                if (option.checked !== undefined)
                    return;
                option.checked = option.default || false;
            });
        });
    }

    State.checklistItems = items;
    State.percentChecklistItemsComplete = Helper.updatePercentage();
}

function resetChecklistToDefault() {
    Helper.setChecklist(defaultChecklistItems);
    State.percentChecklistItemsComplete = 0;
}

function resetCounter() {
    State.count = 0;
    Helper.updateCounterTime();
}

function resetChecklistAndCounter() {
    Helper.resetChecklistToDefault();
    Helper.resetCounter();
}

function updateCounterTime() {
    const dateTime = DateTime.now();
    State.lastUpdated = dateTime.toString();
}

function parseTheme(themeString: string | null): Theme {
    if (!themeString)
        return defaultOptions.theme;

    themeString = themeString.toLowerCase();

    let foundTheme: Theme | null = null;

    allThemes.forEach(theme => {
        if (themeString === theme)
            foundTheme = theme;
    });

    return foundTheme ?? defaultOptions.theme;
}

function toggleTheme() {
    State.options.theme = State.options.theme === "black" ? "light" : State.options.theme === "frappe" ? "black" : "frappe";
    forceCurrentTheme(State.options.theme);
}

function forceTheme(theme: Theme) {
    State.options.theme = theme;
    forceCurrentTheme(State.options.theme);
}

function resetTheme() {
    State.options.theme = defaultOptions.theme;
    forceCurrentTheme(State.options.theme);
}

//Note: All functions with "currentTheme" refer to TempState.theme
function toggleCurrentTheme() {
    const currentThemeIndex = allThemes.indexOf(TempState.theme);
    const nextThemeIndex = (currentThemeIndex + 1) % allThemes.length;
    TempState.theme = allThemes[nextThemeIndex];
    applyTheme();
}

function forceCurrentTheme(theme: Theme) {
    TempState.theme = theme;
    applyTheme();
}

function resetCurrentTheme() {
    TempState.theme = State.options.theme;
    applyTheme();
}

function applyTheme() {
    removeAllThemeClasses().then(() => {
        document.documentElement.classList.add(TempState.theme);
    });
}

//* This function is required because the applyTheme function was adding the new theme class before the old theme class was removed
function removeAllThemeClasses(): Promise<void> {
    return new Promise(resolve => {
        allThemes.forEach(theme => {
            document.documentElement.classList.remove(theme);
        });
        resolve();
    });
}

function resetOptionsToDefault() {
    State.options = defaultOptions;
}

function applyOptions() {
    forceTheme(State.options.theme);
}

function setUpsideDown(upsideDown: boolean) {
    TempState.upsideDown = upsideDown;
    applyUpsideDown();
}

function applyUpsideDown() {
    document.documentElement.classList.remove("upside-down");
    if (TempState.upsideDown) {
        document.documentElement.classList.add("upside-down");
        window.scrollTo(0, document.body.scrollHeight);
    }
}

function timeAgo(date: DateTime): string {
    const now = DateTime.now();
    const diff = now.diff(date, ['days', 'hours', 'minutes', 'seconds']).toObject();

    const days = Math.floor(diff.days || 0);
    const hours = Math.floor(diff.hours || 0);
    const minutes = Math.floor(diff.minutes || 0);
    const seconds = Math.floor(diff.seconds || 0);

    const parts: string[] = [];

    if (days) {
        parts.push(`${days} day${days !== 1 ? 's' : ''}`);
    }
    if (hours) {
        parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
    }
    if (minutes) {
        parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
    }

    // Only show seconds if there are no days or hours
    if (!days && !hours && seconds && seconds > 0) {
        parts.push(`${seconds} second${seconds !== 1 ? 's' : ''}`);
    } else if (seconds && seconds <= 0) {
        return 'just now';
    }

    // If no time has elapsed (or rounding made them all 0), return "just now"
    if (parts.length === 0) {
        return 'just now';
    }

    // Join the parts with commas and "and" where appropriate
    let formatted: string;
    if (parts.length === 1) {
        formatted = parts[0];
    } else if (parts.length === 2) {
        formatted = parts.join(' and ');
    } else {
        formatted = parts.slice(0, -1).join(', ') + ', and ' + parts[parts.length - 1];
    }

    return formatted + ' ago';
}

export const Helper = {
    loadStateFromLocalStorage,
    saveStateToLocalStorage,
    percentageToHexColor,
    updatePercentage,
    updatePercentageEvent,
    setChecklist,
    resetChecklistToDefault,
    parseTheme,
    toggleTheme,
    forceTheme,
    resetTheme,
    toggleCurrentTheme,
    forceCurrentTheme,
    resetCurrentTheme,
    resetOptionsToDefault,
    applyOptions,
    setUpsideDown,
    timeAgo,
    updateCounterTime,
    resetCounter,
    resetChecklistAndCounter,
};